"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Badge, Button, Card, Col, Row, Modal, Spinner } from "react-bootstrap";
import styles from "./news.module.scss";

export interface Article {
  id?: string | number;
  title: string;
  image_url?: string | null;
  publisher?: string | null;
  country?: string | null;
  source_url: string;
  published_date?: string | null;
  summary?: string | null;
  content?: string;
}

interface ApiResponse<T> {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: T[];
}

type Props = {
  records: ApiResponse<Article>;
  isVerified?: boolean;
};

const FALLBACK_IMAGE = "/images/news-fallback.jpg"; 

export default function ArticleList({ records, isVerified }: any) {
  const [pageSize] = useState<number>(8);
  const [apiResponse, setApiResponse] = useState<ApiResponse<Article>>(records);

  const initialCount = Math.min(pageSize, records?.results?.length || 0);
  const [displayCount, setDisplayCount] = useState<number>(initialCount);
  const [currentRecords, setCurrentRecords] = useState<Article[]>(
    records?.results?.slice(0, initialCount) || []
  );
  const [loadingMore, setLoadingMore] = useState(false);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);

  useEffect(() => {
    setApiResponse(records);
    const available = records?.results?.length || 0;
    const firstCount = Math.min(pageSize, available);
    setDisplayCount(firstCount);
    setCurrentRecords(records?.results?.slice(0, firstCount) || []);
  }, [records, pageSize]);

  const nextUrl = apiResponse?.next ?? null;

  const showMore = useCallback(async () => {
    if (currentRecords.length < apiResponse.results.length) {
      const nextRecordsCount = Math.min(
        apiResponse.results.length,
        currentRecords.length + pageSize
      );
      setCurrentRecords(apiResponse.results.slice(0, nextRecordsCount));
      setDisplayCount(nextRecordsCount);
      return;
    }

    if (nextUrl) {
      try {
        setLoadingMore(true);
        const res = await fetch(nextUrl);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data: ApiResponse<Article> = await res.json();

        const updatedResults = [...apiResponse.results, ...(data.results || [])];
        const merged: ApiResponse<Article> = {
          ...apiResponse,
          ...data,
          results: updatedResults,
        };

        setApiResponse(merged);

        const nextCount = Math.min(
          currentRecords.length + pageSize,
          updatedResults.length
        );
        setCurrentRecords(updatedResults.slice(0, nextCount));
        setDisplayCount(nextCount);
      } catch (error) {
        console.error("Failed to load more records:", error);
        toast.error("Couldn’t load more articles. Please try again.");
      } finally {
        setLoadingMore(false);
      }
    }
  }, [apiResponse, currentRecords.length, nextUrl, pageSize]);

  const handleShowDetails = (article: Article) => {
    setSelectedArticle(article);
    setShowModal(true);
  };
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedArticle(null);
  };
  const handleReadMore = () => {
    if (selectedArticle?.source_url) {
      window.open(selectedArticle.source_url, "_blank", "noopener,noreferrer");
      handleCloseModal();
    }
  };

  const options: Intl.DateTimeFormatOptions = useMemo(
    () => ({ year: "numeric", month: "long", day: "numeric" }),
    []
  );

  const hasNoResults =
    !apiResponse?.results?.length || apiResponse?.count === 0;

  const formatDate = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString("en-US", options) : "—";

  const truncate = (s?: string | null, n = 160) =>
    s ? (s.length > n ? s.slice(0, n - 1) + "…" : s) : "";

  const [lead, ...rest] = currentRecords;
  const subleads = rest.slice(0, 2); 
  const standards = rest.slice(2);

  return (
    <>
      <div className="mt-3">
        <Card className={styles.newsCard}>
          <Card.Body
            className="tw-p-4 tw-m-0"
            style={{
              background:
                "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%)",
            }}
          >
            {hasNoResults ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon} aria-hidden />
                <p>No news found</p>
                {isVerified ? (
                  <small className="text-muted">Try adjusting your filters.</small>
                ) : (
                  <small className="text-muted">Check back soon.</small>
                )}
              </div>
            ) : (
              <Row className="tw-p-0 tw-m-0">
                {currentRecords.map((record, index) => {
                  const dateLabel =
                    record.published_date &&
                    new Date(record.published_date).toLocaleDateString(
                      "en-US",
                      options
                    );
                  return (
                    <Col
                      key={record.id ?? `${record.title}-${index}`}
                      xs={12}
                      sm={6}
                      md={4}
                      lg={3}
                      className="tw-p-2 tw-m-0"
                    >
                      <article className={styles.articleCard}>
                        <Card className={styles.cardInner}>
                          <div className={styles.imageWrap}>
                            {record.image_url ? (
                              <img
                                src={record.image_url}
                                alt={record.title || "News image"}
                                className={styles.cardImage}
                                loading={index < 4 ? "eager" : "lazy"}
                                decoding="async"
                                onError={(e) => {
                                  (e.currentTarget as HTMLImageElement).src =
                                    FALLBACK_IMAGE;
                                }}
                              />
                            ) : (
                              <div className={styles.noImage}>
                                <span>No image</span>
                              </div>
                            )}

                            {record.country && (
                              <Badge
                                bg="primary"
                                className={styles.countryBadge}
                                aria-label={`Country: ${record.country}`}
                              >
                                {record.country}
                              </Badge>
                            )}
                          </div>

                          <Card.Body className={styles.cardBody}>
                            <Card.Title className={styles.cardTitle}>
                              {record.title}
                            </Card.Title>
                            <div className={styles.metaRow}>
                              <small className={styles.publisher}>
                                {record.publisher || "—"}
                              </small>
                              <small className={styles.date}>
                                {dateLabel || "—"}
                              </small>
                            </div>
                            <Button
                              variant="outline-primary"
                              onClick={() => handleShowDetails(record)}
                              className={styles.ctaBtn}
                            >
                              Show More
                            </Button>
                          </Card.Body>
                        </Card>
                      </article>
                    </Col>
                  );
                })}
              </Row>
            )}
          </Card.Body>

          {!hasNoResults && (
            <Card.Footer className={styles.footerBar}>
              <div className={styles.footerGrid}>
                <div />
                {apiResponse?.count > displayCount && (
                  <Button
                    onClick={showMore}
                    className={styles.loadMoreBtn}
                    disabled={loadingMore}
                  >
                    {loadingMore ? (
                      <>
                        <Spinner size="sm" animation="border" /> Loading…
                      </>
                    ) : (
                      "Show More"
                    )}
                  </Button>
                )}
                <div className={styles.counter}>
                  {displayCount} / {apiResponse?.count || 0}
                </div>
              </div>
            </Card.Footer>
          )}
        </Card>
      </div>

      <Modal
          show={showModal}
          onHide={handleCloseModal}
          size="lg"
          centered
          scrollable
          dialogClassName={`${styles.newsDialog} ${styles.themeAI2Peat}`}
          className={`${styles.articleModal} ${styles.themeAI2Peat}`}
        >
          <Modal.Header className={styles.modalHeader}>
            <div className={styles.nameplate}>
              <img src="/logo_black.svg" alt="AI2Peat" sizes="100vw" className={styles.logo} width={80} />
              <span className={styles.nameplateTitle}>AI2PEAT GAZETTE</span>
              <span className={styles.edition}>
                {selectedArticle?.published_date
                  ? new Date(selectedArticle.published_date).toLocaleDateString(
                      "en-US",
                      options
                    )
                  : new Date().toLocaleDateString("en-US", options)}
              </span>
            </div>
          </Modal.Header>

          <Modal.Body className={styles.paperBody}>
            {selectedArticle ? (
              <article className={styles.paperArticle}>
                <section className={styles.storyNarrow}>
                  <figure className={`${styles.heroFigure} ${styles.heroClamp}`}>
                    {selectedArticle.image_url ? (
                      <img
                        src={selectedArticle.image_url}
                        alt={selectedArticle.title || "Article image"}
                        className={styles.heroImg}
                        loading="eager"
                        decoding="async"
                        onError={(e) => {
                          (e.currentTarget as HTMLImageElement).src = FALLBACK_IMAGE;
                        }}
                      />
                    ) : (
                      <div className={styles.noImageHero}>No image</div>
                    )}
                  </figure>

                  <h1 className={styles.hed}>{selectedArticle.title}</h1>

                  <div style={{padding:"0px 0px 10px"}}>
                    {selectedArticle.country && (
                      <span className={styles.kicker}>{selectedArticle.country}</span>
                    )}
                    <span className={styles.publisher}>{selectedArticle.publisher || "—"}</span>
                  </div>

                  {selectedArticle.summary && (
                    <p className={styles.dek}>
                      {selectedArticle.summary.length > 260
                        ? selectedArticle.summary.slice(0, 259) + "…"
                        : selectedArticle.summary}
                    </p>
                  )}

                  <div className={styles.storyText}>
                    <p className={styles.lede}>
                      {selectedArticle.content ||
                        selectedArticle.summary ||
                        "No summary available for this article."}
                    </p>
                    {selectedArticle.summary && (
                      <blockquote className={styles.pullQuote}>
                        “{selectedArticle.summary.slice(0, 160)}…”
                      </blockquote>
                    )}
                  </div>
                </section>
              </article>
            ) : (
              <div className={styles.modalSkeleton}>
                <div className={styles.skelImg} />
                <div className={styles.skelLine} />
                <div className={styles.skelLine} />
              </div>
            )}
          </Modal.Body>

          <Modal.Footer className={styles.modalFooter}>
            <Button variant="secondary" onClick={handleCloseModal}>
              Close
            </Button>
            <Button
              variant="primary"
              onClick={handleReadMore}
              disabled={!selectedArticle?.source_url}
            >
              Read full story →
            </Button>
          </Modal.Footer>
        </Modal>
    </>
  );
}